import React from "react";
import { CheckCircle2 } from "lucide-react";

const layers = [
  { title: "Ingestion", items: ["DMARC report routing", "Edge workers", "Async queues", "TLS-RPT + ARF parsing"] },
  { title: "Processing", items: ["SPF chain resolution", "DKIM signature validation", "Spoofing analytics", "Sender classification"] },
  { title: "Storage", items: ["Tenant-scoped Postgres", "Cold report archive (R2)", "Stripe-managed billing", "Audit log ledger"] },
  { title: "Delivery", items: ["Dashboards", "Webhook + REST APIs", "Slack / Teams alerts", "CSV / JSON exports"] },
];

export default function Architecture() {
  return (
    <div>
      <section className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">Architecture</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] ink">
            How trust flows<br /><span className="font-serif italic font-normal">through the platform.</span>
          </h1>
          <p className="mt-7 text-lg ink-soft leading-relaxed">
            A multi-layered, edge-native pipeline engineered for tenant isolation, real-time analysis, and operational integrity.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-3">
            {layers.map((l, i) => (
              <div key={l.title} className="panel p-6" data-testid={`arch-layer-${i}`}>
                <div className="text-xs font-mono ink-muted mb-3">Layer {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-lg font-bold ink">{l.title}</h3>
                <ul className="mt-4 space-y-2">
                  {l.items.map((x) => (
                    <li key={x} className="text-sm ink-soft flex items-start gap-1.5">
                      <span className="text-orange-600 font-bold mt-0.5">›</span>{x}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-3">Trust validation</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight ink">Domain Trust Validation Layer</h2>
            <p className="mt-4 ink-soft max-w-2xl mx-auto">
              Every domain must complete DNS-based ownership verification before any telemetry is collected, processed, or surfaced.
            </p>
          </div>
          <div className="panel overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider ink-soft">Control</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider ink-soft">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {[
                  ["DNS TXT verification", "Proves domain ownership"],
                  ["Tenant-bound domain mapping", "Prevents cross-tenant access"],
                  ["Verification expiration checks", "Ensures continued control"],
                  ["Audit logging", "Tracks onboarding actions"],
                  ["Scoped ingestion routing", "Prevents unauthorized report ingestion"],
                ].map(([c, p]) => (
                  <tr key={c}>
                    <td className="p-4 font-medium ink">{c}</td>
                    <td className="p-4 ink-soft">{p}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>



      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-4">
          {[
            { t: "Validation & Hardening", items: ["RBAC validation", "Webhook signature checks", "Replay protection", "Parser hardening", "Tenant isolation testing", "API fuzzing"] },
            { t: "Operational Boundaries", items: ["Stripe handles all PCI data", "Never stores cards / CVV / tokens", "Encrypted reports at rest", "Scoped API access", "Audit log retention 12mo", "MFA-enforced admin paths"] },
          ].map((b) => (
            <div key={b.t} className="panel p-7">
              <h3 className="font-display text-xl font-bold ink">{b.t}</h3>
              <ul className="mt-5 space-y-2.5">
                {b.items.map((i) => (
                  <li key={i} className="flex items-center gap-2 text-sm ink-soft">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
